import type { HtmlFromDotcms } from './converters-from-dotcms';
import {
  convertDotcmsBoolean,
  convertDotcmsEnumValue,
  convertDotcmsResponseToRecord,
  wrapDotcmsHtml,
  wrapDotcmsHtmlOrUndefined,
} from './converters-from-dotcms';
import type { IBaseObjectData } from './dotcms-base-object';
import { BlockEditorGqlHelper } from './dotcms-block-editor';
import type { IImageData } from './dotcms-image';
import { ImageGqlHelper } from './dotcms-image';
import type { ILinkBasedOnEnrollmentStatusData } from './dotcms-link-based-on-enrollment-status';
import { LinkBasedOnEnrollmentStatusGqlHelper } from './dotcms-link-based-on-enrollment-status';
import { buildGqlFieldsQuery } from './gql-builders';
import type { IGqlHelper } from './gql-helper';

export enum ImageHorizontalAlignment {
  Left = 'left',
  Right = 'right',
}

export type IModuleWithImageData = IBaseObjectData & {
  header?: HtmlFromDotcms;
  headerCentered: boolean;
  headerMaximized: boolean;
  content: HtmlFromDotcms;
  contentIfTerminatedUser?: HtmlFromDotcms;
  checkmarksList?: HtmlFromDotcms[];
  button?: ILinkBasedOnEnrollmentStatusData;
  showImage: boolean;
  imageHorizontalAlignment: ImageHorizontalAlignment;
  image?: IImageData;
  footnote?: HtmlFromDotcms | undefined;
};

export const ModuleWithImageGqlHelper: IGqlHelper<IModuleWithImageData> = {
  contentTypeName: 'ModuleWithImage',
  gqlFieldsQuery: `
    header
    isHeaderCentered
    isHeaderMaximized
    content
    contentIfTerminatedUser
    checkmarksList { ${buildGqlFieldsQuery(BlockEditorGqlHelper)} }
    linkBasedOnEnrollmentStatus { ${buildGqlFieldsQuery(
    LinkBasedOnEnrollmentStatusGqlHelper,
  )} }
    showImage
    imageHorizontalAlignment
    image { ${buildGqlFieldsQuery(ImageGqlHelper)}}
    footnote
  `,
  processGqlData: (gqlResponse: unknown) => {
    if (!gqlResponse) {
      return undefined;
    }

    const {
      header,
      isHeaderCentered,
      isHeaderMaximized,
      content,
      contentIfTerminatedUser,
      checkmarksList,
      linkBasedOnEnrollmentStatus,
      showImage,
      imageHorizontalAlignment,
      image,
      footnote,
    } = convertDotcmsResponseToRecord(gqlResponse);
    const imageData = ImageGqlHelper.processGqlData(image);

    return {
      header: wrapDotcmsHtml(header),
      headerCentered: convertDotcmsBoolean(isHeaderCentered) ?? false,
      headerMaximized: convertDotcmsBoolean(isHeaderMaximized) ?? false,
      content: wrapDotcmsHtml(content) ?? { __html: '' },
      contentIfTerminatedUser: wrapDotcmsHtmlOrUndefined(
        contentIfTerminatedUser,
      ),
      checkmarksList:
        BlockEditorGqlHelper.processGqlDataAsArrayOfHtmls(checkmarksList),
      button: LinkBasedOnEnrollmentStatusGqlHelper.processGqlData(
        linkBasedOnEnrollmentStatus,
      ),
      showImage: convertDotcmsBoolean(showImage) ?? imageData === undefined,
      imageHorizontalAlignment:
        convertDotcmsEnumValue(
          ImageHorizontalAlignment,
          imageHorizontalAlignment,
        ) ?? ImageHorizontalAlignment.Left,
      image: imageData,
      footnote: wrapDotcmsHtmlOrUndefined(footnote),
    };
  },
};
